/**
 * Client ID provider for API requests - Server-Side Proxy Version
 *
 * Security improvement: API keys are now handled server-side only
 * Frontend never sees sensitive keys, server proxy injects client_id automatically
 *
 * Logic:
 * - V5 endpoints: send user's client_id via x-user-client-id header (if available)
 * - All endpoints: server proxy handles client_id injection automatically
 */

interface ClientIdContext {
  endpoint?: string;
  userClientId?: string;
}

/**
 * @deprecated This function is no longer used with server-side proxy
 * Use getApiConfig() instead for proper server-side proxy configuration
 *
 * Kept for backward compatibility - but server proxy handles all client_id logic now
 */
export function getClientId(context: ClientIdContext = {}): string | null {
  const { endpoint = '', userClientId } = context;

  // V5 endpoints with user context
  if (endpoint.includes('/v5/') && userClientId) {
    return userClientId; // Return the actual user client ID
  }

  // For V1 endpoints, we would need the server API key, but that's not available in frontend anymore
  // This is why we moved to server-side proxy
  return null;
}

/**
 * Get API configuration with proper headers for server-side proxy
 * This is the main function used by the new proxy system
 */
export function getApiConfig(
  context: ClientIdContext = {}
): Record<string, any> {
  const { endpoint = '', userClientId } = context;

  const config: Record<string, any> = {};

  // For V5 endpoints with user context, send user client_id via custom header
  if (endpoint.includes('/v5/') && userClientId) {
    config.headers = {
      'x-user-client-id': userClientId,
    };
  }

  return config;
}

/**
 * Helper for backward compatibility with existing code
 * @deprecated Use getApiConfig() directly instead
 */
export function withUserContext(userClientId: string): Record<string, any> {
  return {
    headers: {
      'x-user-client-id': userClientId,
    },
  };
}
